import React, { useState, useEffect, useRef } from "react";
import DateRangePicker from "react-bootstrap-daterangepicker";
import moment from "moment";
import "bootstrap/dist/css/bootstrap.css";
import "../../assets/scss/datepicker.css";
import { leaveManagamentPlaceholder } from "services/constants/PlaceHolder";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { TextField, InputAdornment, IconButton } from "@mui/material";

const CustomDateRangePicker = ({
  handleStartDate,
  showToday,
  triggerDate,
  includeCurrentDate,
  fromDate,
  toDate,
  allowFutureDate = false,
  hideLabel = false,
  compact = false,
}) => {
  const DATE_DISPLAY_FORMAT = "DD-MM-YYYY";
  const DATE_PARSE_FORMATS = ["DD-MM-YYYY", "DD/MM/YYYY"];
  const [state, setState] = useState(() => {
    if (fromDate && toDate) {
      return {
        start: moment(fromDate, DATE_PARSE_FORMATS),
        end: moment(toDate, DATE_PARSE_FORMATS),
      };
    }
    if (!triggerDate) {
      const today = moment();
      return { start: today.clone(), end: today.clone() };
    } else {
      // When triggerDate is true and includeCurrentDate is requested,
      // default to the current date (today) instead of the last 7 days.
      if (includeCurrentDate) {
        const today = moment();
        return { start: today.clone(), end: today.clone() };
      }
      // Fallback: keep the previous behavior (last 7 days excluding today)
      return {
        start: moment().subtract(1, "days"),
        end: moment().subtract(1, "days"),
      };
    }
  });
  const [trigger, setTrigger] = useState(true);
  const [labelName, setLabelName] = useState(
    triggerDate ? (includeCurrentDate ? "Today" : "Yesterday") : "Today",
  );
  const pickerRef = useRef(null);
  const [popupOpen, setPopupOpen] = useState(false);

  // The underlying jQuery plugin repositions its popup on window resize
  // (its own internal move(), wired to the resize event) but never on
  // scroll, so scrolling a container the trigger sits in while the popup is
  // open leaves it visually stuck in its old spot. Re-run the same move()
  // on any scroll while open. `capture: true` is required to observe scroll
  // on a nested scrollable ancestor, since plain scroll events don't bubble.
  useEffect(() => {
    if (!popupOpen) return undefined;
    const reposition = () => {
      pickerRef.current?.$picker?.data?.("daterangepicker")?.move?.();
    };
    document.addEventListener("scroll", reposition, true);
    return () => document.removeEventListener("scroll", reposition, true);
  }, [popupOpen]);

  useEffect(() => {
    if (fromDate && toDate) {
      setState({
        start: moment(fromDate, DATE_PARSE_FORMATS),
        end: moment(toDate, DATE_PARSE_FORMATS),
      });
      setLabelName(`Date Range`);
      return; // Skip default reset
    }
    const getPastDayDetails = () => {
      if (triggerDate) {
        const defaultStart = moment().subtract(1, "days");
        const defaultEnd = moment().subtract(1, "days");

        if (includeCurrentDate) {
          // default to today
          const today = moment();
          setState({ start: today, end: today });
          setLabelName("Today");
          handleStartDate(
            today.format(DATE_DISPLAY_FORMAT),
            today.format(DATE_DISPLAY_FORMAT),
          );
        } else {
          setState({ start: defaultStart, end: defaultEnd });
          setLabelName("Yesterday");
          handleStartDate(
            defaultStart.format(DATE_DISPLAY_FORMAT),
            defaultEnd.format(DATE_DISPLAY_FORMAT),
          );
        }
      } else {
        const today = moment();
        setState({ start: today, end: today });
        setLabelName(triggerDate ? "Last 7 Days" : "Today");
        handleStartDate(today.format(DATE_DISPLAY_FORMAT), today.format(DATE_DISPLAY_FORMAT));
      }
    };
    if (trigger) {
      getPastDayDetails();
      setTrigger(false);
    }
  }, [
    handleStartDate,
    includeCurrentDate,
    trigger,
    triggerDate,
    fromDate,
    toDate,
  ]);

  const { start, end } = state;
  const handleCallback = (start, end, rangeLabel) => {
    setState({ start, end });
    handleStartDate(
      moment(new Date(start)).format(DATE_DISPLAY_FORMAT),
      moment(new Date(end)).format(DATE_DISPLAY_FORMAT),
    );
    setLabelName(rangeLabel);
  };
  const label = `${start.format(DATE_DISPLAY_FORMAT)} - ${end.format(DATE_DISPLAY_FORMAT)}`;

  const getRanges = () => ({
    ...(showToday && {
      Today: [moment().toDate(), moment().toDate()],
    }),
    Yesterday: [
      moment().subtract(1, "days").toDate(),
      moment().subtract(1, "days").toDate(),
    ],
    "Last 7 Days": includeCurrentDate
      ? [moment().subtract(6, "days").toDate(), moment()]
      : [
          moment().subtract(7, "days").toDate(),
          moment().subtract(1, "days").toDate(),
        ],
    "Last 30 Days": [moment().subtract(29, "days").toDate(), moment().toDate()],
    "This Month": [
      moment().startOf("month").toDate(),
      moment().endOf("month").toDate(),
    ],
    "Last Month": [
      moment().subtract(1, "month").startOf("month").toDate(),
      moment().subtract(1, "month").endOf("month").toDate(),
    ],
  });

  return (
    <DateRangePicker
      initialSettings={{
        startDate: start.toDate(),
        endDate: end.toDate(),
        ranges: getRanges(),
        // maxDate: showToday
        //   ? moment().toDate()
        //   : moment().subtract(1, "days").toDate(),
        ...(allowFutureDate
          ? {}
          : {
              maxDate: showToday
                ? moment().toDate()
                : moment().subtract(1, "days").toDate(),
            }),
        applyClass: "custom-apply-btn",
        cancelClass: "custom-cancel-btn",
        locale: {
          format: DATE_DISPLAY_FORMAT,
        },
      }}
      onCallback={handleCallback}
      onShow={() => setPopupOpen(true)}
      onHide={() => setPopupOpen(false)}
      ref={pickerRef}
    >
      <TextField
        id="reportrange"
        variant="outlined"
        {...(hideLabel
          ? {}
          : {
              label: labelName,
              InputLabelProps: { shrink: true, required: true },
            })}
        placeholder={leaveManagamentPlaceholder.dateFormat}
        value={label}
        size="small"
        sx={
          compact
            ? {
                width: 268,
                maxWidth: "100%",
                "& .MuiInputBase-root": {
                  height: 45,
                  borderRadius: "4px",
                  fontSize: 14,
                },
                "& .MuiInputBase-input": {
                  padding: "12px 0 12px 13px",
                },
              }
            : undefined
        }
        InputProps={{
          readOnly: true,
          sx: {
            boxSizing: "border-box",
            borderRadius: compact ? "4px" : "8px",
            "& fieldset": {
              borderColor: compact ? "#333333" : "#D7E0EA",
              borderWidth: 1,
            },
            "&:hover fieldset": {
              borderColor: compact ? "#333333" : "#A5B4C7",
            },
            "&.Mui-focused fieldset": {
              borderColor: compact ? "#333333" : "#0D9488",
            },
          },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size={compact ? "small" : "medium"} aria-label="Select date range">
                <DateRangeIcon sx={compact ? { fontSize: 20 } : undefined} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        fullWidth={!compact}
      />
    </DateRangePicker>
  );
};

export default CustomDateRangePicker;
